import { openDB } from "idb";
import { useSyncExternalStore } from "react";
import { api, uuid } from "@/lib/api";

const dbp = typeof indexedDB !== "undefined" ? openDB("oneeverett-pos", 1, {
  upgrade(db) {
    db.createObjectStore("meta");
    db.createObjectStore("stock", { keyPath: "batch_id" });
    db.createObjectStore("sales", { keyPath: "offline_id" }).createIndex("state", "state");
  },
}) : null;

let state = { online: typeof navigator === "undefined" ? true : navigator.onLine, status: "idle", pending: 0, error: "", lastSync: null, enrolled: false };
const subs = new Set();
const emit = (patch) => { state = { ...state, ...patch }; subs.forEach((f) => f()); };
export const useOfflineStatus = () => useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => state);

export const getMeta = async (k) => (await dbp).get("meta", k);
const setMeta = async (k, v) => (await dbp).put("meta", v, k);
const hdrs = (t) => ({ "X-Till-Id": t.id, "X-Till-Secret": t.secret });

export async function enrolDevice(till, secret) { await setMeta("till", { id: till.id, code: till.code, name: till.name, secret }); await refreshCounts(); }
export async function forgetDevice() {
  const db = await dbp;
  if ((await db.getAllFromIndex("sales", "state", "pending")).length) throw new Error("Sync pending offline sales before removing this till");
  await db.clear("stock"); await db.delete("meta", "till"); await db.delete("meta", "snapshot"); await refreshCounts();
}
export async function clearSession() { if (dbp) { await (await dbp).delete("meta", "snapshot"); } }

export async function refreshCounts() {
  if (!dbp) return;
  const db = await dbp;
  emit({ pending: (await db.getAllFromIndex("sales", "state", "pending")).length, enrolled: !!(await db.get("meta", "till")) });
}

export async function refreshSnapshot() {
  const db = await dbp;
  const till = await db.get("meta", "till");
  if (!till) throw new Error("This device is not enrolled as a till");
  const { data } = await api.get("/tills/device/snapshot", { headers: hdrs(till) });
  const pending = await db.getAllFromIndex("sales", "state", "pending");
  const local = {};
  pending.forEach((s) => s.lines.forEach((l) => { local[l.batch_id] = (local[l.batch_id] || 0) + l.qty; }));
  const tx = db.transaction(["stock", "meta"], "readwrite");
  await tx.objectStore("stock").clear();
  for (const s of data.stock) await tx.objectStore("stock").put({ ...s, remaining: s.remaining - (local[s.batch_id] || 0) });
  await tx.objectStore("meta").put({ ...data, stock: undefined }, "snapshot");
  await tx.done;
  return data;
}

export async function localStock() { return (await dbp).getAll("stock"); }
export async function localSales() { return (await (await dbp).getAll("sales")).sort((a, b) => b.created_at.localeCompare(a.created_at)); }

// One IndexedDB transaction: stock decrement + receipt number + sale record commit together or not at all.
export async function recordSale({ lines, payments, total }) {
  const db = await dbp;
  const snap = await db.get("meta", "snapshot");
  const till = await db.get("meta", "till");
  const tx = db.transaction(["stock", "sales", "meta"], "readwrite");
  try {
    for (const l of lines) {
      const s = await tx.objectStore("stock").get(l.batch_id);
      if (!s || s.remaining < l.qty) throw new Error(`Only ${s ? s.remaining : 0} ${l.name} left in this till's allowance`);
      await tx.objectStore("stock").put({ ...s, remaining: s.remaining - l.qty });
    }
    const seq = ((await tx.objectStore("meta").get("seq")) || 0) + 1;
    await tx.objectStore("meta").put(seq, "seq");
    const sale = { offline_id: uuid(), session_token: snap.session_token, created_at: new Date().toISOString(), local_receipt_no: `OFF-${till.code}-${String(seq).padStart(5, "0")}`,
      lines, payments, total, cashier: snap.session.name, till_code: till.code, state: "pending" };
    await tx.objectStore("sales").put(sale);
    await tx.done;
    await refreshCounts();
    return sale;
  } catch (e) { try { tx.abort(); } catch { /* already aborted */ } throw e; }
}

let syncing = false;
export async function syncNow() {
  if (!dbp || syncing || !navigator.onLine) return;
  const db = await dbp;
  const till = await db.get("meta", "till");
  if (!till) return;
  const pending = await db.getAllFromIndex("sales", "state", "pending");
  if (!pending.length) { emit({ status: "idle", error: "" }); return; }
  syncing = true; emit({ status: "syncing", error: "" });
  try {
    for (let i = 0; i < pending.length; i += 50) {
      const chunk = pending.slice(i, i + 50);
      const { data } = await api.post("/tills/device/sync", { sales: chunk.map(({ state: _s, total: _t, cashier: _c, till_code: _tc, lines, ...s }) => ({ ...s, lines: lines.map(({ name: _n, ...l }) => l) })) }, { headers: hdrs(till) });
      for (const r of data.results) {
        const s = await db.get("sales", r.offline_id);
        await db.put("sales", { ...s, state: r.result === "rejected" ? "rejected" : "synced", number: r.number, flags: r.flags || [], error: r.error || "", synced_at: new Date().toISOString() });
      }
    }
    const rejected = (await db.getAll("sales")).filter((s) => s.state === "rejected").length;
    emit({ status: rejected ? "failed" : "idle", error: rejected ? `${rejected} sale(s) rejected — open Offline tills for details` : "", lastSync: new Date().toISOString() });
  } catch (e) {
    emit({ status: "failed", error: e.response ? `Server: ${e.response.data?.detail || e.response.status}` : "Network error — will retry automatically" });
  } finally { syncing = false; await refreshCounts(); }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => { emit({ online: true }); syncNow(); });
  window.addEventListener("offline", () => emit({ online: false }));
  setInterval(() => syncNow(), 30000);
  refreshCounts().then(() => syncNow());
}
